import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, Facebook, Instagram } from "lucide-react";

const FACEBOOK_URL = "https://www.facebook.com/share/19NotLSiCH/?mibextid=wwXIfr";
const INSTAGRAM_URL = "https://www.instagram.com/roleplay_connect";

export function PublicHeader() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const links = [
    { label: "How it works", href: "#how" },
    { label: "Features", href: "#features" },
    { label: "Pricing", href: "#pricing" },
  ];
  return (
    <header className="sticky top-0 z-40 border-b border-border/70 bg-background/80 backdrop-blur-md">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo />
        <nav className="hidden items-center gap-8 md:flex">
          {links.map((l) => (
            <a key={l.href} href={l.href} className="text-sm font-medium text-muted-foreground transition-colors hover:text-foreground">{l.label}</a>
          ))}
        </nav>
        <div className="hidden items-center gap-2 md:flex">
          <a
            href={FACEBOOK_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow Role-play Connect on Facebook"
            data-testid="header-facebook-link"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Facebook className="h-5 w-5" />
          </a>
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Follow Role-play Connect on Instagram"
            data-testid="header-instagram-link"
            className="inline-flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-primary/10 hover:text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <Instagram className="h-5 w-5" />
          </a>
          {user ? (
            <Button data-testid="header-open-app" onClick={() => navigate("/app")}>Open app</Button>
          ) : (
            <>
              <Button variant="ghost" data-testid="header-login" onClick={() => navigate("/login")}>Log in</Button>
              <Button data-testid="header-get-started" onClick={() => navigate("/register")}>Get started</Button>
            </>
          )}
        </div>
        <div className="md:hidden">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-trigger"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="mt-6 flex flex-col gap-4">
                {links.map((l) => (
                  <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="text-base font-medium text-foreground">{l.label}</a>
                ))}
                <div className="mt-4 flex flex-col gap-2">
                  {user ? (
                    <Button onClick={() => navigate("/app")}>Open app</Button>
                  ) : (
                    <>
                      <Button variant="outline" onClick={() => navigate("/login")}>Log in</Button>
                      <Button onClick={() => navigate("/register")}>Get started</Button>
                    </>
                  )}
                </div>
                <a
                  href={FACEBOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-facebook-link"
                  className="mt-2 inline-flex items-center gap-2 text-base font-medium text-foreground"
                >
                  <Facebook className="h-5 w-5 text-primary" /> Follow us on Facebook
                </a>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-instagram-link"
                  className="inline-flex items-center gap-2 text-base font-medium text-foreground"
                >
                  <Instagram className="h-5 w-5 text-primary" /> Follow us on Instagram
                </a>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
